import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { AdditionalDetails } from './wizard-steps/additional-details';
import { ContactInformation } from './wizard-steps/contact-information';
import { GeneralInformation } from './wizard-steps/general-information';
import { NextMeeting } from './wizard-steps/next-meeting';
import { PaymentLandscape } from './wizard-steps/payment-landscape';

interface Step {
  id: string;
  title: string;
  description: string;
  component: React.ComponentType;
  completed?: boolean;
}

const steps: Step[] = [
  {
    id: 'general',
    title: 'General Information',
    description: 'Basic company details',
    component: GeneralInformation,
  },
  {
    id: 'contact',
    title: 'Contact Information',
    description: 'Primary contacts and communication',
    component: ContactInformation,
  },
  {
    id: 'payment',
    title: 'Payment Landscape',
    description: 'Payment methods and preferences',
    component: PaymentLandscape,
  },
  {
    id: 'additional',
    title: 'Additional Details',
    description: 'Extra information and notes',
    component: AdditionalDetails,
  },
  {
    id: 'meeting',
    title: 'Next Meeting',
    description: 'Schedule follow-up',
    component: NextMeeting,
  },
];

interface ProspectWizardProps {
  companyId: string;
  companyName: string;
  onComplete: () => void;
}

export function ProspectWizard({ companyName, onComplete }: ProspectWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const contentRef = useRef<HTMLDivElement>(null);

  const handleNext = () => {
    setCompletedSteps((prev) => new Set([...prev, currentStep]));
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepIndex: number) => {
    setCurrentStep(stepIndex);
  };

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, []);

  const CurrentStepComponent = steps[currentStep].component;
  const isLastStep = currentStep === steps.length - 1;

  return (
    <div className="flex-1 flex flex-col bg-background">
      <div className="border-b border-border pb-4 mb-6">
        <h1 className="text-2xl font-semibold">Setup Prospect Information</h1>
        <p className="text-muted-foreground">
          Configure prospect details, contact information, and preferences for {companyName}
        </p>
      </div>

      <div className="border-b border-border pb-6 mb-6">
        <div className="flex items-center justify-center space-x-6 max-w-6xl mx-auto">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <button
                type="button"
                onClick={() => handleStepClick(index)}
                className={`flex flex-col items-center text-center p-4 rounded-xl w-40 h-32 transition-all duration-200 ${
                  index === currentStep
                    ? 'bg-gray-900 text-white shadow-lg'
                    : completedSteps.has(index)
                      ? 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100'
                      : 'bg-muted/50 text-muted-foreground border border-border hover:bg-muted hover:text-foreground'
                }`}
              >
                <div
                  className={`w-6 h-6 inline-flex shrink-0 items-center justify-center rounded-full mb-3 aspect-square ${
                    index === currentStep
                      ? 'bg-white text-gray-900'
                      : completedSteps.has(index)
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-300 text-gray-600'
                  }`}
                >
                  {completedSteps.has(index) ? (
                    <Check className="h-[14px] w-[14px] stroke-2" />
                  ) : (
                    <span className="text-[13px] font-bold leading-none">{index + 1}</span>
                  )}
                </div>

                <div>
                  <div className="font-medium text-sm leading-tight mb-1">{step.title}</div>
                  <div className="text-xs opacity-75 leading-tight">{step.description}</div>
                </div>
              </button>

              {index < steps.length - 1 && (
                <div
                  className={`w-6 h-0.5 mx-3 ${
                    index < currentStep ? 'bg-green-300' : 'bg-gray-300'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 mb-6" ref={contentRef}>
        <div className="max-w-4xl mx-auto animate-fade-slide-in" key={currentStep}>
          <div className="mb-4">
            <h2 className="text-lg font-semibold">{steps[currentStep].title}</h2>
            <p className="text-sm text-muted-foreground">{steps[currentStep].description}</p>
          </div>
          <CurrentStepComponent />
        </div>
      </div>

      <div className="border-t border-border pt-6">
        <div className="flex justify-between max-w-4xl mx-auto">
          <Button variant="outline" onClick={handlePrevious} disabled={currentStep === 0}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          <div className="flex gap-2">
            <Button variant="outline">Save Draft</Button>
            <Button
              onClick={handleNext}
              className="dark:bg-sidebar-primary dark:text-sidebar-primary-foreground dark:hover:bg-sidebar-primary/90"
            >
              {isLastStep ? 'Complete Setup' : 'Next'}
              {!isLastStep && <ChevronRight className="w-4 h-4 ml-2" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
